import { useNavigate } from "@solidjs/router";
import type { WebsiteHealthSummary } from "contracts/api/website/responses";
import { createEffect, createSignal, For, Show } from "solid-js";
import { matchesQuery } from "../../boundaries/text-search.search";
import type { KeyboardScope } from "../../enums/shell";
import { KEYBOARD_SCOPE } from "../../enums/shell";
import { setScope } from "../../keyboard/manager";
import { registerBinding } from "../../keyboard/registry";
import { api } from "../../store/api";
import { PaletteItem } from "./paletteItems";

const [open, setOpen] = createSignal(false);

export function registerPaletteBinding(): void {
  registerBinding({
    combo: "mod+k",
    scope: KEYBOARD_SCOPE.GLOBAL,
    describe: "Open command palette",
    run: () => void openPalette(),
  });
}

async function openPalette(): Promise<void> {
  setOpen(true);
}

export function PaletteHost() {
  const navigate = useNavigate();
  const [websites, setWebsites] = createSignal<WebsiteHealthSummary[]>([]);
  const [query, setQuery] = createSignal("");
  const [selected, setSelected] = createSignal(0);
  let previousScope: KeyboardScope = KEYBOARD_SCOPE.LIST;
  let input!: HTMLInputElement;

  const filtered = () =>
    websites().filter((site) =>
      matchesQuery(query(), site.displayName, site.clientName, site.domain),
    );

  createEffect(() => {
    if (!open()) {
      return;
    }
    previousScope = KEYBOARD_SCOPE.LIST;
    setScope(KEYBOARD_SCOPE.PALETTE);
    setQuery("");
    setSelected(0);
    void api.listWebsites().then(setWebsites);
    queueMicrotask(() => input?.focus());
  });

  createEffect(() => {
    if (selected() >= filtered().length) {
      setSelected(Math.max(0, filtered().length - 1));
    }
  });

  const close = () => {
    setOpen(false);
    setScope(previousScope);
  };
  const choose = (site: WebsiteHealthSummary) => {
    close();
    navigate(`/w/${site.websiteId}`);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setSelected(Math.min(filtered().length - 1, selected() + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setSelected(Math.max(0, selected() - 1));
    } else if (event.key === "Enter") {
      const site = filtered()[selected()];
      if (site) {
        choose(site);
      }
    } else if (event.key === "Escape") {
      close();
    }
  };

  return (
    <Show when={open()}>
      <div class="palette-scrim">
        <div class="palette">
          <input
            ref={input}
            class="palette-input"
            placeholder="Search websites…"
            value={query()}
            onInput={(event) => {
              setQuery(event.currentTarget.value);
              setSelected(0);
            }}
            onKeyDown={onKeyDown}
          />
          <div class="palette-list">
            <Show
              when={filtered().length > 0}
              fallback={<div class="palette-empty">No matches</div>}
            >
              <For each={filtered()}>
                {(site, index) => (
                  <PaletteItem
                    site={site}
                    selected={index() === selected()}
                    onChoose={() => choose(site)}
                  />
                )}
              </For>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}
